import React, { useState, useEffect, useCallback } from "react";
import { Plus, Sun } from "lucide-react";
import { AddSunbedModal } from "../../components/sunbeds/AddSunbedModal";
import { DeleteConfirmationModal } from "../../components/sunbeds/DeleteConfirmationModal";
import { Button } from "../../components/ui/Button";

import { SubPageHeader } from "../../components/ui/SubPageHeader";
import { SunbedActionToolbar } from "../../components/sunbeds/SunbedActionToolbar";
import { PaginationFooter } from "../../components/sunbeds/PaginationFooter";
import { SunbedsTable, SunbedRoom } from "../../components/sunbeds/SunbedsTable";
import {
  createSunbedRoom,
  getSunbedRooms,
  updateSunbedRoom,
  deleteSunbedRoom,
} from "../../services/sunbeds";
import { useAuth } from "../../auth/AuthContext";
import { hasSunbedCoolingEnabled } from "../../lib/sunbedCooling";

const ROOMS_POLL_MS = 60_000;

function mapRoomStatus(raw: string): SunbedRoom["status"] {
  const normalized = raw.trim().toLowerCase().replace(/_/g, "-").replace(/\s+/g, "-");
  if (normalized === "available") return "Available";
  if (normalized === "in-session") return "In-session";
  if (normalized === "maintenance") return "Maintenance";
  if (normalized === "cleaning") return "Cleaning";
  if (normalized === "cooling") return "Cooling";
  const titled = raw.charAt(0).toUpperCase() + raw.slice(1).toLowerCase();
  if (titled === "In-session") return "In-session";
  return titled as SunbedRoom["status"];
}

const SunbedsPage: React.FC = () => {
  const { user } = useAuth();
  const coolingEnabled = hasSunbedCoolingEnabled(user);

  const [rooms, setRooms] = useState<SunbedRoom[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(8);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingRoom, setEditingRoom] = useState<SunbedRoom | null>(null);
  const [modalMode, setModalMode] = useState<"add" | "edit">("add");
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const fetchRooms = useCallback(async (opts?: { silent?: boolean }) => {
    const silent = Boolean(opts?.silent);
    if (!silent) setLoading(true);
    try {
      const response = await getSunbedRooms();
      if (response && response.data) {
        const mappedRooms: SunbedRoom[] = response.data.map((item: any) => ({
          id: item.id.toString(),
          label: item.label_formatted || item.label,
          status: mapRoomStatus(String(item.availability_formatted || item.availability || "")),
          totalDuration: item.total_hours_formatted || "0 mins",
        }));
        setRooms(mappedRooms);
      }
    } catch (error) {
      console.error("Failed to fetch sunbed rooms", error);
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRooms();
  }, [fetchRooms]);

  /** When cooling is on, refresh rooms every minute so scheduler-driven availability updates appear. */
  useEffect(() => {
    if (!coolingEnabled) return;

    const id = window.setInterval(() => {
      void fetchRooms({ silent: true });
    }, ROOMS_POLL_MS);

    return () => window.clearInterval(id);
  }, [coolingEnabled, fetchRooms]);

  const filteredRooms = rooms.filter((r) =>
    r.label.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const paginatedRooms = filteredRooms.slice(indexOfFirstItem, indexOfLastItem);

  React.useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  const handleAdd = () => {
    setModalMode("add");
    setEditingRoom(null);
    setIsModalOpen(true);
  };

  const handleEdit = (room: SunbedRoom) => {
    setModalMode("edit");
    setEditingRoom(room);
    setIsModalOpen(true);
  };

  const handleSave = async (label: string) => {
    try {
      if (modalMode === "add") {
        const response = await createSunbedRoom({ label });
        if (response.success || response.message) {
          fetchRooms();
        }
      } else if (editingRoom) {
        const response = await updateSunbedRoom(editingRoom.id, { label });
        if (response.success || response.message) {
          fetchRooms();
        }
      }
      setIsModalOpen(false);
    } catch (error) {
      console.error("Failed to save sunbed room", error);
    }
  };

  const handleDelete = (id: string) => {
    setDeleteId(id);
    setIsDeleteOpen(true);
  };

  const confirmDelete = async () => {
    if (deleteId) {
      try {
        await deleteSunbedRoom(deleteId);
        fetchRooms();
      } catch (error) {
        console.error("Failed to delete room", error);
      }
      setDeleteId(null);
    }
    setIsDeleteOpen(false);
  };

  return (
    <div>
      <div className="flex-1 flex flex-col space-y-6 overflow-hidden">
        <SubPageHeader
          title="Sunbeds"
          subtitle="Manage your sunbed rooms, availability, and usage stats"
          icon={Sun}
          actions={
            <Button
              onClick={handleAdd}
              className="bg-[#885ABB] hover:bg-[#7748A4] text-white flex items-center gap-2 px-5 py-2.5 shadow-sm hover:shadow-md transition-all duration-200"
            >
              <Plus size={18} />
              Create New
            </Button>
          }
        />

        <SunbedActionToolbar
          totalItems={rooms.length}
          showingStart={paginatedRooms.length > 0 ? indexOfFirstItem + 1 : 0}
          showingEnd={Math.min(indexOfLastItem, filteredRooms.length)}
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          searchPlaceholder="Search rooms..."
        />

        <div className="flex-1 rounded-xl border border-gray-200 shadow-sm bg-white overflow-hidden flex flex-col">
          <SunbedsTable
            rooms={paginatedRooms}
            indexOfFirstItem={indexOfFirstItem}
            onEdit={handleEdit}
            onDelete={handleDelete}
            isLoading={loading}
          />

          {!loading && (
            <PaginationFooter
              totalItems={rooms.length}
              filteredTotal={filteredRooms.length}
              currentPage={currentPage}
              itemsPerPage={itemsPerPage}
              onPageChange={setCurrentPage}
            />
          )}
        </div>
      </div>

      <AddSunbedModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={handleSave}
        existingLabel={editingRoom?.label}
        mode={modalMode}
      />

      <DeleteConfirmationModal
        isOpen={isDeleteOpen}
        onClose={() => setIsDeleteOpen(false)}
        onConfirm={confirmDelete}
        title="Delete Room"
        message="Are you sure you want to delete this sunbed room?"
      />
    </div>
  );
};

export default SunbedsPage;
